import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "../../hooks/useConfirm";
import { 
  Package, Plus, Pencil, Trash2, Search, Filter, 
  ChevronRight, ChevronDown, Scale, Flame, TrendingUp, TrendingDown,
  History, X, Tags, CheckSquare, Square, LayoutGrid, List, Users, Check, AlertCircle, AlertTriangle
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { IngredientiFormDialog, IngredientiDetailDialog } from "../../components/anagrafica/IngredientiDialogs";
import { ToggleSwitch as Switch } from "../../components/ui/toggle-switch";
import { Checkbox } from "../../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ingredienti = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [ingredienti, setIngredienti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [search, setSearch] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("tutte");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form per aggiungere prezzo manualmente
  const [showAddPrezzo, setShowAddPrezzo] = useState(false);
  const [prezzoForm, setPrezzoForm] = useState({
    fornitore_id: "",
    prezzo_kg: ""
  });
  const [savingPrezzo, setSavingPrezzo] = useState(false);
  
  const [form, setForm] = useState({
    nome: "",
    categoria_id: "",
    unita_misura: "kg",
    prezzo_kg: "",
    fornitore_id: "",
    cotto_default: false,
    calo_cottura_percentuale: 0,
    note: ""
  });

  // Selezione multipla
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Vista: raggruppata per categoria, lista semplice, o per fornitore
  const [viewMode, setViewMode] = useState("grouped"); // "grouped", "list", "supplier"
  const [expandedCategories, setExpandedCategories] = useState({}); // Categorie espanse
  
  // Analisi per fornitore
  const [selectedFornitore, setSelectedFornitore] = useState(null);
  const [analisiFornitore, setAnalisiFornitore] = useState(null);

  useEffect(() => {
    if (localeCorrente?.id) {
      fetchData();
    }
  }, [localeCorrente?.id]);

  const fetchData = async () => {
    if (!localeCorrente?.id) return;
    
    try {
      // Init categorie se non esistono
      await axios.post(`${API}/categorie-ingredienti/init?locale_id=${localeCorrente.id}`, {}, { headers: getAuthHeaders() });
      
      const [ingredientiRes, categorieRes, fornitoriRes] = await Promise.all([
        axios.get(`${API}/ingredienti?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categorie-ingredienti?locale_id=${localeCorrente.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/fornitori`, { headers: getAuthHeaders() })
      ]);
      
      setIngredienti(ingredientiRes.data);
      setCategorie(categorieRes.data);
      setFornitori(fornitoriRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        nome: item.nome || "",
        categoria_id: item.categoria_id || "",
        unita_misura: item.unita_misura || "kg",
        prezzo_kg: "",
        fornitore_id: "",
        cotto_default: item.cotto_default || false,
        calo_cottura_percentuale: item.calo_cottura_percentuale || 0,
        note: item.note || ""
      });
    } else {
      setEditItem(null);
      setForm({
        nome: "",
        categoria_id: "",
        unita_misura: "kg",
        prezzo_kg: "",
        fornitore_id: "",
        cotto_default: false,
        calo_cottura_percentuale: 0,
        note: ""
      });
    }
    setDialogOpen(true);
  };

  // Deep-link dagli alert: /anagrafica/ingredienti?open={id} apre e scrolla all'ingrediente
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || !ingredienti.length) return;
    const item = ingredienti.find((i) => i.id === openId);
    if (!item) return;
    openDialog(item);
    setTimeout(() => {
      document.querySelector(`[data-testid="ingrediente-card-${openId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredienti, searchParams]);

  const openDetail = async (item) => {
    try {
      // Carica prezzi filtrati per locale corrente (include anche prezzi globali)
      const response = await axios.get(
        `${API}/ingredienti/${item.id}/prezzi-fornitori?locale_id=${localeCorrente?.id}`, 
        { headers: getAuthHeaders() }
      );
      setSelectedItem({ ...item, prezziDetail: response.data });
      setShowAddPrezzo(false);
      setPrezzoForm({ fornitore_id: "", prezzo_kg: "" });
      setDetailOpen(true);
    } catch (error) {
      toast.error("Errore nel caricamento dettagli");
    }
  };

  const handleAddPrezzo = async () => {
    if (!prezzoForm.fornitore_id || !prezzoForm.prezzo_kg) {
      toast.error("Seleziona fornitore e inserisci il prezzo");
      return;
    }

    const fornitoreSelezionato = fornitori.find(f => f.id === prezzoForm.fornitore_id);
    if (!fornitoreSelezionato) {
      toast.error("Fornitore non trovato");
      return;
    }

    setSavingPrezzo(true);
    try {
      await axios.post(
        `${API}/ingredienti/${selectedItem.id}/prezzo`,
        {
          ingrediente_id: selectedItem.id,
          fornitore_id: prezzoForm.fornitore_id,
          fornitore_nome: fornitoreSelezionato.ragione_sociale,
          prezzo_kg: parseFloat(prezzoForm.prezzo_kg),
          locale_id: localeCorrente?.id || null  // Prezzo specifico per il locale corrente
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Prezzo aggiunto con successo per questo locale");
      
      // Ricarica i dettagli con filtro per locale
      const response = await axios.get(
        `${API}/ingredienti/${selectedItem.id}/prezzi-fornitori?locale_id=${localeCorrente?.id}`, 
        { headers: getAuthHeaders() }
      );
      setSelectedItem({ ...selectedItem, prezziDetail: response.data });
      setShowAddPrezzo(false);
      setPrezzoForm({ fornitore_id: "", prezzo_kg: "" });
      
      // Aggiorna anche la lista ingredienti
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiunta del prezzo");
    } finally {
      setSavingPrezzo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        locale_id: localeCorrente.id,
        nome: form.nome,
        categoria_id: form.categoria_id || null,
        unita_misura: form.unita_misura,
        cotto_default: form.cotto_default,
        calo_cottura_percentuale: parseFloat(form.calo_cottura_percentuale) || 0,
        note: form.note || null
      };

      if (editItem) {
        await axios.put(`${API}/ingredienti/${editItem.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Materia prima aggiornata");
      } else {
        // Aggiungi prezzo se fornito
        if (form.prezzo_kg && form.fornitore_id) {
          payload.prezzo_kg = parseFloat(form.prezzo_kg);
          payload.fornitore_id = form.fornitore_id;
        }
        await axios.post(`${API}/ingredienti`, payload, { headers: getAuthHeaders() });
        toast.success("Materia prima creata");
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Sei sicuro di voler eliminare questa materia prima?")) return;

    try {
      await axios.delete(`${API}/ingredienti/${id}`, { headers: getAuthHeaders() });
      toast.success("Materia prima eliminata");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const getCategoriaNome = (id) => categorie.find(c => c.id === id)?.nome || "Senza categoria";

  // Funzioni selezione multipla
  const toggleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === ingredientiFiltrati.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(ingredientiFiltrati.map(i => i.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.length === 0) return;
    
    if (!await confirm(`Sei sicuro di voler eliminare ${selectedItems.length} materie prime?`)) return;

    try {
      await Promise.all(
        selectedItems.map(id => axios.delete(`${API}/ingredienti/${id}`, { headers: getAuthHeaders() }))
      );
      toast.success(`${selectedItems.length} materie prime eliminate`);
      setSelectedItems([]);
      setSelectionMode(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedItems([]);
  };

  // Filtra ingredienti
  const ingredientiFiltrati = ingredienti.filter(i => {
    const matchSearch = !search || i.nome.toLowerCase().includes(search.toLowerCase());
    const matchCategoria = filtroCategoria === "tutte" || i.categoria_id === filtroCategoria;
    return matchSearch && matchCategoria;
  });

  // Raggruppa ingredienti per categoria
  const ingredientiPerCategoria = ingredientiFiltrati.reduce((acc, ing) => {
    const catId = ing.categoria_id || "senza-categoria";
    if (!acc[catId]) {
      acc[catId] = [];
    }
    acc[catId].push(ing);
    return acc;
  }, {});

  // Ordina le categorie (quelle con più ingredienti prima)
  const categorieOrdinate = Object.keys(ingredientiPerCategoria).sort((a, b) => {
    return ingredientiPerCategoria[b].length - ingredientiPerCategoria[a].length;
  });

  // Toggle espansione categoria
  const toggleCategoria = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  // Espandi/Collassa tutte
  const expandAll = () => {
    const allExpanded = {};
    categorieOrdinate.forEach(catId => {
      allExpanded[catId] = true;
    });
    setExpandedCategories(allExpanded);
  };

  const collapseAll = () => {
    setExpandedCategories({});
  };

  // Calcola analisi prezzi per un fornitore specifico
  const calcolaAnalisiFornitore = (fornitoreId) => {
    if (!fornitoreId) {
      setAnalisiFornitore(null);
      return;
    }

    const fornitore = fornitori.find(f => f.id === fornitoreId);
    if (!fornitore) return;

    const analisi = {
      fornitore: fornitore,
      prodotti_in_uso: [],
      prodotti_non_in_uso: [],
      totale_prodotti: 0
    };

    // Per ogni ingrediente, controlla se il fornitore ha un prezzo
    ingredienti.forEach(ing => {
      const prezzi = ing.prezzi_fornitori || [];
      
      // Raggruppa prezzi per fornitore e prendi solo il più recente/basso per ogni fornitore
      const prezziPerFornitore = {};
      prezzi.forEach(p => {
        const fId = p.fornitore_id;
        if (!prezziPerFornitore[fId] || p.prezzo_kg < prezziPerFornitore[fId].prezzo_kg) {
          prezziPerFornitore[fId] = p;
        }
      });
      
      const prezzoFornitore = prezziPerFornitore[fornitoreId];
      
      if (prezzoFornitore) {
        // Il fornitore ha questo prodotto
        // Trova il prezzo più basso tra ALTRI fornitori (escluso quello che stiamo analizzando)
        const prezziAltriForitori = Object.entries(prezziPerFornitore)
          .filter(([fId]) => fId !== fornitoreId)
          .map(([, p]) => p);
        
        const prezzoMinimoAltri = prezziAltriForitori.length > 0 
          ? Math.min(...prezziAltriForitori.map(p => p.prezzo_kg || Infinity))
          : null;
        
        // È in uso se: non ci sono altri fornitori OPPURE il prezzo è il migliore
        const isInUso = prezzoMinimoAltri === null || prezzoFornitore.prezzo_kg <= prezzoMinimoAltri;
        
        // Trova chi ha il prezzo migliore tra gli altri fornitori
        let fornitoreConPrezzoMigliore = null;
        if (!isInUso && prezziAltriForitori.length > 0) {
          const prezzoMigliore = prezziAltriForitori.find(p => p.prezzo_kg === prezzoMinimoAltri);
          if (prezzoMigliore) {
            fornitoreConPrezzoMigliore = fornitori.find(f => f.id === prezzoMigliore.fornitore_id);
          }
        }

        const prodotto = {
          ingrediente: ing,
          prezzo_fornitore: prezzoFornitore.prezzo_kg,
          prezzo_migliore: prezzoMinimoAltri || prezzoFornitore.prezzo_kg,
          differenza: prezzoMinimoAltri ? prezzoFornitore.prezzo_kg - prezzoMinimoAltri : 0,
          fornitore_migliore: fornitoreConPrezzoMigliore,
          is_in_uso: isInUso
        };

        if (isInUso) {
          analisi.prodotti_in_uso.push(prodotto);
        } else {
          analisi.prodotti_non_in_uso.push(prodotto);
        }
        analisi.totale_prodotti++;
      }
    });

    // Ordina per nome ingrediente
    analisi.prodotti_in_uso.sort((a, b) => a.ingrediente.nome.localeCompare(b.ingrediente.nome));
    analisi.prodotti_non_in_uso.sort((a, b) => b.differenza - a.differenza); // Prima i più cari

    setAnalisiFornitore(analisi);
  };

  // Effetto per calcolare l'analisi quando cambia il fornitore selezionato
  useEffect(() => {
    if (viewMode === "supplier" && selectedFornitore) {
      calcolaAnalisiFornitore(selectedFornitore);
    }
  }, [selectedFornitore, ingredienti, viewMode]);

  // Inizializza tutte le categorie come espanse al primo caricamento
  useEffect(() => {
    if (categorieOrdinate.length > 0 && Object.keys(expandedCategories).length === 0) {
      expandAll();
    }
  }, [ingredienti]);

  if (loading) {
    return (
      <div className="loading-state">
        <div className="text-muted-custom">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="ingredienti-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Materie Prime</h1>
          <p className="page-subtitle">Gestisci le materie prime e i loro prezzi</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectionMode ? (
            <>
              <Button 
                variant="outline" 
                onClick={exitSelectionMode}
                className="border-border text-foreground hover:bg-muted"
              >
                <X className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Annulla</span>
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteSelected}
                disabled={selectedItems.length === 0}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="delete-selected-btn"
              >
                <Trash2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Elimina</span> ({selectedItems.length})
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => setSelectionMode(true)}
                className="border-border text-foreground hover:bg-muted"
                data-testid="selection-mode-btn"
              >
                <CheckSquare className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Seleziona</span>
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/anagrafica/ingredienti/categorie?tipo=ingredienti')}
                className="border-border text-foreground hover:bg-muted"
              >
                <Tags className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Categorie</span>
              </Button>
              <Button onClick={() => openDialog()} className="bg-primary hover:bg-primary/90 text-white" data-testid="add-ingrediente-btn">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nuovo</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-col gap-3">
        {/* Barra ricerca e filtro categoria */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca ingrediente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-card border-border text-foreground"
              data-testid="search-input"
            />
          </div>
          {viewMode !== "supplier" && (
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-full sm:w-48 bg-card border-border text-foreground" data-testid="filter-categoria">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="tutte" className="text-foreground">Tutte le categorie</SelectItem>
                {categorie.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-foreground">{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        
        {/* Toggle vista - Tabs mobile-friendly */}
        <div className="flex w-full bg-muted/50 rounded-xl p-1 gap-1">
          <button
            onClick={() => setViewMode("grouped")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
              viewMode === "grouped" 
                ? "bg-card text-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            <span className="hidden sm:inline">Categorie</span>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
              viewMode === "list" 
                ? "bg-card text-foreground shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">Lista</span>
          </button>
          <button
            onClick={() => setViewMode("supplier")}
            data-testid="view-supplier-btn"
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
              viewMode === "supplier" 
                ? "bg-primary text-white shadow-sm" 
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Users className="h-4 w-4" />
            <span>Trattativa</span>
          </button>
        </div>
      </div>

      {/* Selettore fornitore - visibile solo in modalità "supplier" */}
      {viewMode === "supplier" && (
        <div className="space-y-3">
          {/* Dropdown fornitore - full width su mobile */}
          <Select value={selectedFornitore || ""} onValueChange={setSelectedFornitore}>
            <SelectTrigger className="w-full bg-card border-border text-foreground h-12" data-testid="select-fornitore-analisi">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <SelectValue placeholder="Seleziona fornitore per trattativa..." />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-card border-border max-h-80">
              {fornitori
                .filter(f => f.ragione_sociale)
                .sort((a, b) => a.ragione_sociale.localeCompare(b.ragione_sociale))
                .map(f => (
                  <SelectItem key={f.id} value={f.id} className="text-foreground py-3">
                    {f.ragione_sociale}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          
          {/* Stats compatte */}
          {analisiFornitore && (
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                <div className="text-2xl font-bold text-green-400">{analisiFornitore.prodotti_in_uso.length}</div>
                <div className="text-xs text-green-400/80">In uso</div>
              </div>
              <div className="text-center p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <div className="text-2xl font-bold text-red-400">{analisiFornitore.prodotti_non_in_uso.length}</div>
                <div className="text-xs text-red-400/80">Non in uso</div>
              </div>
              <div className="text-center p-3 bg-muted border border-border rounded-xl">
                <div className="text-2xl font-bold text-foreground">{analisiFornitore.totale_prodotti}</div>
                <div className="text-xs text-muted-foreground">Totale</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Barra info e azioni rapide */}
      {viewMode === "grouped" && filtroCategoria === "tutte" && (
        <div className="flex items-center justify-between bg-muted/30 rounded-xl px-4 py-2">
          <span className="text-sm text-muted-foreground">
            {ingredientiFiltrati.length} ingredienti in {categorieOrdinate.length} categorie
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={expandAll} className="text-xs text-muted-foreground hover:text-foreground">
              Espandi tutto
            </Button>
            <Button variant="ghost" size="sm" onClick={collapseAll} className="text-xs text-muted-foreground hover:text-foreground">
              Collassa tutto
            </Button>
          </div>
        </div>
      )}

      {/* Lista Materie Prime */}
      <div className="space-y-4">
        {viewMode === "supplier" ? (
          /* VISTA ANALISI PER FORNITORE */
          <>
            {!selectedFornitore ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Seleziona un fornitore per vedere l'analisi prezzi</p>
              </div>
            ) : !analisiFornitore ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Caricamento analisi...</p>
              </div>
            ) : analisiFornitore.totale_prodotti === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nessun prodotto trovato per questo fornitore</p>
                <p className="text-sm mt-2">Aggiungi prezzi per questo fornitore dalla scheda ingrediente</p>
              </div>
            ) : (
              <>
                {/* Prodotti IN USO */}
                {analisiFornitore.prodotti_in_uso.length > 0 && (
                  <div className="bg-card/50 border border-green-500/30 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-green-500/10 border-b border-green-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                          <Check className="h-5 w-5 text-green-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-green-400">Prodotti IN USO</h3>
                          <p className="text-xs text-muted-foreground">
                            {analisiFornitore.prodotti_in_uso.length} prodotti con il miglior prezzo
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-border/50">
                      {analisiFornitore.prodotti_in_uso.map((item) => (
                        <div
                          key={item.ingrediente.id}
                          className="flex items-center justify-between p-3 px-4 hover:bg-muted/20 transition-colors cursor-pointer"
                          onClick={() => openDetail(item.ingrediente)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 bg-green-400/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Package className="h-4 w-4 text-green-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">{item.ingrediente.nome}</p>
                              <p className="text-xs text-muted-foreground">
                                {getCategoriaNome(item.ingrediente.categoria_id)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-green-400">
                              €{item.prezzo_fornitore.toFixed(2)}/{item.ingrediente.unita_misura}
                            </p>
                            <p className="text-xs text-green-400/70">Miglior prezzo</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); openDetail(item.ingrediente); setShowAddPrezzo(true); }}
                            className="h-8 w-8 text-muted-foreground hover:text-foreground ml-2"
                            title="Aggiungi nuovo prezzo"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prodotti NON IN USO */}
                {analisiFornitore.prodotti_non_in_uso.length > 0 && (
                  <div className="bg-card/50 border border-red-500/30 rounded-2xl overflow-hidden">
                    <div className="p-4 bg-red-500/10 border-b border-red-500/20">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                          <TrendingUp className="h-5 w-5 text-red-400" />
                        </div>
                        <div>
                          <h3 className="font-bold text-red-400">Prodotti da negoziare</h3>
                          <p className="text-sm text-muted-foreground">
                            {analisiFornitore.prodotti_non_in_uso.length} prodotti con prezzo più alto di altri fornitori
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="divide-y divide-border/50">
                      {analisiFornitore.prodotti_non_in_uso.map((item) => (
                        <div
                          key={item.ingrediente.id}
                          className="p-4 hover:bg-muted/20 transition-colors cursor-pointer"
                          onClick={() => openDetail(item.ingrediente)}
                        >
                          {/* Layout mobile-first: stack verticale */}
                          <div className="flex items-start justify-between gap-3">
                            {/* Info prodotto */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 bg-amber-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Package className="h-5 w-5 text-amber-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-foreground">{item.ingrediente.nome}</p>
                                <p className="text-xs text-muted-foreground">
                                  {getCategoriaNome(item.ingrediente.categoria_id)}
                                </p>
                              </div>
                            </div>
                            {/* Pulsante aggiungi */}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); openDetail(item.ingrediente); setShowAddPrezzo(true); }}
                              className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-muted"
                              title="Aggiungi nuovo prezzo"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {/* Confronto prezzi - Layout chiaro */}
                          <div className="mt-3 flex items-stretch gap-2">
                            {/* Prezzo fornitore analizzato (più alto) */}
                            <div className="flex-1 bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">
                              <p className="text-xs text-red-400 font-medium mb-1">Prezzo attuale</p>
                              <p className="text-lg font-bold text-red-400">
                                €{item.prezzo_fornitore.toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">/{item.ingrediente.unita_misura}</p>
                            </div>
                            
                            {/* Freccia */}
                            <div className="flex items-center text-muted-foreground">
                              <ChevronRight className="h-5 w-5" />
                            </div>
                            
                            {/* Prezzo migliore (altro fornitore) */}
                            <div className="flex-1 bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-center">
                              <p className="text-xs text-green-400 font-medium mb-1 truncate">
                                {item.fornitore_migliore?.ragione_sociale || 'Altro'}
                              </p>
                              <p className="text-lg font-bold text-green-400">
                                €{item.prezzo_migliore.toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">/{item.ingrediente.unita_misura}</p>
                            </div>
                          </div>
                          
                          {/* Badge differenza */}
                          <div className="mt-2 flex justify-center">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/10 text-amber-400 text-sm font-medium rounded-full">
                              <TrendingDown className="h-3.5 w-3.5" />
                              Risparmio potenziale: €{item.differenza.toFixed(2)}/{item.ingrediente.unita_misura}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : ingredientiFiltrati.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Nessuna materia prima trovata</p>
          </div>
        ) : viewMode === "grouped" && filtroCategoria === "tutte" ? (
          /* VISTA RAGGRUPPATA PER CATEGORIA */
          <>
            {/* Seleziona tutto - visibile solo in modalità selezione */}
            {selectionMode && (
              <div 
                className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl border border-border cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={toggleSelectAll}
                data-testid="select-all-row"
              >
                <Checkbox 
                  checked={selectedItems.length === ingredientiFiltrati.length && ingredientiFiltrati.length > 0}
                  onCheckedChange={toggleSelectAll}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="text-foreground font-medium">
                  {selectedItems.length === ingredientiFiltrati.length ? "Deseleziona tutto" : "Seleziona tutto"}
                </span>
                <span className="text-muted-foreground text-sm ml-auto">
                  {selectedItems.length} di {ingredientiFiltrati.length} selezionati
                </span>
              </div>
            )}
            
            {categorieOrdinate.map(catId => {
              const categoria = categorie.find(c => c.id === catId);
              const nomeCategoria = categoria?.nome || "Senza categoria";
              const ingredientiCategoria = ingredientiPerCategoria[catId];
              const isExpanded = expandedCategories[catId];
              const totalePrezzo = ingredientiCategoria.reduce((sum, ing) => sum + (ing.prezzo_attuale || 0), 0);
              
              return (
                <div key={catId} className="bg-card/50 border border-border rounded-2xl overflow-hidden">
                  {/* Header categoria - cliccabile per espandere/collassare */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleCategoria(catId)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isExpanded ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-primary" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground">{nomeCategoria}</h3>
                        <p className="text-xs text-muted-foreground">
                          {ingredientiCategoria.length} {ingredientiCategoria.length === 1 ? 'ingrediente' : 'ingredienti'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Valore medio</p>
                      <p className="font-bold text-lime-400">
                        €{(totalePrezzo / ingredientiCategoria.length).toFixed(2)}/kg
                      </p>
                    </div>
                  </div>
                  
                  {/* Lista ingredienti della categoria */}
                  {isExpanded && (
                    <div className="border-t border-border divide-y divide-border/50">
                      {ingredientiCategoria.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-3 px-4 hover:bg-muted/20 transition-colors cursor-pointer ${
                            selectedItems.includes(item.id) ? 'bg-primary/10' : ''
                          }`}
                          onClick={() => selectionMode ? toggleSelectItem(item.id) : openDetail(item)}
                          data-testid={`ingrediente-card-${item.id}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {selectionMode && (
                              <Checkbox 
                                checked={selectedItems.includes(item.id)}
                                onCheckedChange={() => toggleSelectItem(item.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                              />
                            )}
                            <div className="w-8 h-8 bg-lime-400/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Package className="h-4 w-4 text-lime-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-foreground truncate">{item.nome}</h4>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-muted-foreground">
                                  <Scale className="h-3 w-3 inline mr-1" />
                                  {item.unita_misura}
                                </span>
                                {item.cotto_default && (
                                  <span className="text-xs text-orange-400">
                                    <Flame className="h-3 w-3 inline mr-1" />
                                    -{item.calo_cottura_percentuale}%
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="font-bold text-lime-400">
                                €{(item.prezzo_attuale || 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">/{item.unita_misura}</p>
                            </div>
                            
                            {!selectionMode && (
                              <div className="flex gap-1 ml-2">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={(e) => { e.stopPropagation(); openDialog(item); }}
                                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                                  className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          /* VISTA LISTA SEMPLICE */
          <>
            {/* Seleziona tutto - visibile solo in modalità selezione */}
            {selectionMode && (
              <div 
                className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl border border-border cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={toggleSelectAll}
                data-testid="select-all-row"
              >
                <Checkbox 
                  checked={selectedItems.length === ingredientiFiltrati.length && ingredientiFiltrati.length > 0}
                  onCheckedChange={toggleSelectAll}
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="text-foreground font-medium">
                  {selectedItems.length === ingredientiFiltrati.length ? "Deseleziona tutto" : "Seleziona tutto"}
                </span>
                <span className="text-muted-foreground text-sm ml-auto">
                  {selectedItems.length} di {ingredientiFiltrati.length} selezionati
                </span>
              </div>
            )}
            
            <div className="grid gap-2">
            {ingredientiFiltrati.map((item) => (
              <div
                key={item.id}
                className={`bg-card border rounded-2xl p-4 hover:bg-muted/30 transition-colors cursor-pointer ${
                  selectedItems.includes(item.id) ? 'border-primary bg-primary/5' : 'border-border'
                }`}
                onClick={() => selectionMode ? toggleSelectItem(item.id) : openDetail(item)}
                data-testid={`ingrediente-card-${item.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Checkbox in modalità selezione */}
                    {selectionMode && (
                      <Checkbox 
                        checked={selectedItems.includes(item.id)}
                        onCheckedChange={() => toggleSelectItem(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                    )}
                    
                    {/* Icon */}
                    <div className="w-12 h-12 bg-lime-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Package className="h-6 w-6 text-lime-400" />
                    </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate">{item.nome}</h3>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <span className="text-xs px-2 py-0.5 bg-muted rounded-full text-white/60">
                        {getCategoriaNome(item.categoria_id)}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-muted rounded-full text-white/60">
                        <Scale className="h-3 w-3 inline mr-1" />
                        {item.unita_misura}
                      </span>
                      {item.cotto_default && (
                        <span className="text-xs px-2 py-0.5 bg-orange-500/20 rounded-full text-orange-400">
                          <Flame className="h-3 w-3 inline mr-1" />
                          -{item.calo_cottura_percentuale}%
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Prezzo */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-bold text-lime-400">
                      €{(item.prezzo_attuale || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">/{item.unita_misura}</p>
                    {/* Alert prezzo migliore */}
                    {item.alert_prezzo_migliore && (
                      <div className="mt-1 px-2 py-1 bg-amber-500/20 rounded-lg text-amber-400 text-xs flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        <span>-{item.alert_prezzo_migliore.risparmio_percentuale}% da {item.alert_prezzo_migliore.fornitore_nome?.split(' ')[0]}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-1 ml-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={(e) => { e.stopPropagation(); openDialog(item); }}
                    className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                    className="h-9 w-9 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <ChevronRight className="h-5 w-5 text-white/30 self-center" />
                </div>
              </div>
            </div>
          ))}
          </div>
          </>
        )}
      </div>

      {/* Dialog Crea/Modifica */}
      <IngredientiFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editItem={editItem}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        saving={saving}
        categorie={categorie}
        fornitori={fornitori}
      />

      {/* Dialog Dettaglio Prezzi */}
      <IngredientiDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        selectedItem={selectedItem}
        localeCorrente={localeCorrente}
        fornitori={fornitori}
        showAddPrezzo={showAddPrezzo}
        setShowAddPrezzo={setShowAddPrezzo}
        prezzoForm={prezzoForm}
        setPrezzoForm={setPrezzoForm}
        onAddPrezzo={handleAddPrezzo}
        savingPrezzo={savingPrezzo}
      />
    </div>
  );
};

export default Ingredienti;
